import React, { useEffect, useRef } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DescriptionIcon from "@mui/icons-material/Description";
// import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, Button, CircularProgress, Menu, MenuItem, Stack, Typography } from "@mui/material";

import { ArticleList } from "../../components/News-feed/components/ArticleList";
import { BriefingCard } from "../../components/News-feed/components/BriefingCard";
// import { RoleComponentGuard } from "components";

import { DigestList } from "../../components/News-feed/components/DigestList";
import { NewsFeedFilters } from "../../components/News-feed/components/NewsFeedFilters";
import { FeedTab, NewsFeedProvider, useNewsFeed } from "../../components/News-feed/context/NewsFeedContext";

// import { RoleName } from "../../constants";

export const NewsFeed: React.FC = () => (
  <NewsFeedProvider>
    <NewsFeedPageContent />
  </NewsFeedProvider>
);

const NewsFeedPageContent: React.FC = () => {
  const {
    articles,
    articlesHasMore,
    articlesLoading,
    loadMoreArticles,
    digestHistory,
    briefing,
    feedTab,
    setFeedTab,
    openConfigureFeed,
    // openDealCoverageDialog,
    // openCoverageManager,
    openDigestSettings,
    openBriefing,
    openDigestHighlights
  } = useNewsFeed();

  const [settingsAnchor, setSettingsAnchor] = React.useState<null | HTMLElement>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (feedTab !== FeedTab.LiveFeed) return;
    if (!articlesHasMore) return;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreArticles();
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [feedTab, articlesHasMore, loadMoreArticles, articles.length]);

  return (
    <Stack
      direction="column"
      sx={{
        flex: 1,
        overflow: "hidden"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: "40px",
          borderBottom: 1,
          borderColor: "#dbe8f5",
          px: "20px",
          flexShrink: 0,
          bgcolor: "#fff",
          borderRadius: "8px 8px 0px 0px"
        }}
      >
        <Stack direction="row" alignItems="center" gap="10px">
          <Button
            type="button"
            disableRipple
            onClick={() => setFeedTab(FeedTab.LiveFeed)}
            sx={{
              py: "9px",
              px: "14px",
              pb: "7px",
              borderBottom: "2px solid",
              borderColor: feedTab === FeedTab.LiveFeed ? "primary.main" : "transparent",
              color: feedTab === FeedTab.LiveFeed ? "primary.main" : "secondary.main",
              fontSize: 14,
              fontWeight: feedTab === FeedTab.LiveFeed ? 500 : 400,
              borderRadius: 0,
              textTransform: "none",
              bgcolor: "transparent",
              minWidth: 0,
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: "transparent",
                color: feedTab === FeedTab.LiveFeed ? "primary.main" : "primary.main"
              }
            }}
          >
            Live Feed
          </Button>
          <Button
            type="button"
            disableRipple
            onClick={() => setFeedTab(FeedTab.PastDigests)}
            sx={{
              py: "9px",
              px: "14px",
              pb: "7px",
              borderBottom: "2px solid",
              borderColor: feedTab === FeedTab.PastDigests ? "primary.main" : "transparent",
              color: feedTab === FeedTab.PastDigests ? "primary.main" : "secondary.main",
              fontSize: 14,
              fontWeight: feedTab === FeedTab.PastDigests ? 500 : 400,
              borderRadius: 0,
              textTransform: "none",
              bgcolor: "transparent",
              minWidth: 0,
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: "transparent",
                color: "primary.main"
              }
            }}
          >
            Past Digests
          </Button>
        </Stack>

        <Stack direction="row" alignItems="center" gap="10px">
          <Button
            variant="outlined"
            disableElevation
            startIcon={<SettingsIcon sx={{ fontSize: 15, color: "#5d7782" }} />}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 18, color: "#5d7782" }} />}
            onClick={(e) => setSettingsAnchor(e.currentTarget)}
            sx={{
              height: 30,
              minHeight: 30,
              borderRadius: "100px",
              px: "9px",
              pl: "12px",
              py: 0,
              fontSize: 12,
              fontWeight: 500,
              textTransform: "none",
              color: "#071f35",
              bgcolor: "#fff",
              border: `1px solid ${"#dbe8f5"}`,
              boxShadow: "none",
              minWidth: 0,
              gap: "3px",
              lineHeight: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
              "& .MuiButton-endIcon": { ml: "3px", mr: 0 },
              "&:hover": {
                bgcolor: "#f6f8fc",
                border: `1px solid ${"#dbe8f5"}`,
                boxShadow: "none"
              }
            }}
          >
            Feed Settings
          </Button>
          <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}>
            <MenuItem
              onClick={() => {
                setSettingsAnchor(null);
                void openConfigureFeed();
              }}
            >
              <TuneIcon fontSize="sm" sx={{ mr: 1 }} /> Feed configuration
            </MenuItem>
            {/* <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <MenuItem
                onClick={() => {
                  setSettingsAnchor(null);
                  openCoverageManager();
                }}
              >
                <ManageSearchIcon fontSize="sm" sx={{ mr: 1 }} /> Deal coverage
              </MenuItem>
            </RoleComponentGuard> */}
            <MenuItem
              onClick={() => {
                setSettingsAnchor(null);
                void openDigestSettings();
              }}
            >
              <ScheduleIcon fontSize="sm" sx={{ mr: 1 }} /> Digest settings
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {feedTab === FeedTab.LiveFeed ? <NewsFeedFilters /> : null}

      <Stack
        ref={scrollRef}
        direction="column"
        sx={{ flex: 1, overflow: "auto", bgcolor: "#f6f8fc", py: 1, paddingRight: "15px" }}
      >
        {feedTab === FeedTab.LiveFeed ? (
          <>
            <BriefingCard briefing={briefing} onOpenFull={openBriefing} />
            {articles.length ? (
              <>
                <ArticleList
                  articles={articles}
                  // onDealCoverage={openDealCoverageDialog}
                />
                {articlesHasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
                {articlesLoading && (
                  <Stack alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </Stack>
                )}
              </>
            ) : articlesLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <DescriptionIcon sx={{ width: "22px", height: "22px", fontSize: 48, color: "#797979", mb: 1 }} />
                <Typography variant="h6">No news available right now</Typography>
                <Typography variant="body2" color="text.secondary">
                  Topics are set, but no articles passed the relevance filter for this cycle. Try adjusting your topic
                  configuration.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <DigestList rows={digestHistory} onOpenHighlights={openDigestHighlights} />
        )}
      </Stack>
    </Stack>
  );
};
